let googleScriptPromise = null;
let appleScriptPromise = null;
let runtimeGoogleClientId = '';
let runtimeAppleClientId = '';

/** Public client IDs from GET /api/auth/oauth/config. Build-time env is the fallback. */
export function applyOAuthPublicConfig(config = {}) {
  runtimeGoogleClientId = String(config.googleClientId || '').trim();
  runtimeAppleClientId = String(config.appleClientId || '').trim();
}

function loadScript(src, { id, check } = {}) {
  if (typeof check === 'function' && check()) {
    return Promise.resolve();
  }
  if (id) {
    const existing = document.getElementById(id);
    if (existing) {
      return new Promise((resolve, reject) => {
        if (typeof check === 'function' && check()) {
          resolve();
          return;
        }
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)));
      });
    }
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    if (id) script.id = id;
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

function randomState(bytes = 16) {
  const arr = new Uint8Array(bytes);
  crypto.getRandomValues(arr);
  return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
}

export function loadGoogleIdentityScript() {
  if (!googleScriptPromise) {
    googleScriptPromise = loadScript('https://accounts.google.com/gsi/client', {
      id: 'velox-google-gsi',
      check: () => Boolean(window.google?.accounts?.id),
    });
  }
  return googleScriptPromise;
}

export function loadAppleIdScript() {
  if (!appleScriptPromise) {
    appleScriptPromise = loadScript(
      'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js',
      {
        id: 'velox-apple-id',
        check: () => Boolean(window.AppleID?.auth),
      }
    );
  }
  return appleScriptPromise;
}

export function getGoogleClientId() {
  return (runtimeGoogleClientId || process.env.REACT_APP_GOOGLE_CLIENT_ID || '').trim();
}

export function getAppleClientId() {
  return (runtimeAppleClientId || process.env.REACT_APP_APPLE_CLIENT_ID || '').trim();
}

/**
 * Apple Return URL must be the current page origin (exact match for the
 * web_message callback). In production that is https://velox-network.com.
 */
export function getAppleRedirectUri() {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return (process.env.REACT_APP_APPLE_REDIRECT_URI || '').trim();
}

export function mapAppleAuthError(err) {
  const code = err?.error || err?.code || '';
  const message = err?.message || err?.error_description || '';
  const blob = `${code} ${message}`.toLowerCase();

  if (
    blob.includes('popup_closed') ||
    blob.includes('user_cancelled') ||
    blob.includes('user canceled') ||
    blob.includes('cancelled') ||
    blob.includes('canceled')
  ) {
    return null;
  }

  if (blob.includes('invalid_client') || blob.includes('invalid client')) {
    return 'Apple could not verify this app. Check that APPLE_CLIENT_ID is your Services ID.';
  }

  if (blob.includes('invalid_request') || blob.includes('redirect')) {
    return 'Apple rejected the Return URL. Confirm it matches Apple Developer → Services ID → Return URLs.';
  }

  if (message) return message;
  return 'Apple sign-in could not be completed. Please try again.';
}

/**
 * Render Google's official control into `hitLayer` (invisible overlay so our
 * custom face stays visible while the real click hits GIS).
 */
export function renderGoogleButton(hitLayer, { onCredential, onError, text = 'continue_with' } = {}) {
  const clientId = getGoogleClientId();
  if (!clientId || !hitLayer) {
    return Promise.resolve(() => {});
  }

  return loadGoogleIdentityScript().then(() => {
    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: (response) => {
        if (response?.credential) {
          onCredential?.(response.credential);
        } else {
          onError?.(new Error('Google did not return a credential'));
        }
      },
      auto_select: false,
    });

    const width = Math.max(hitLayer.clientWidth || hitLayer.parentElement?.clientWidth || 320, 240);
    hitLayer.innerHTML = '';
    window.google.accounts.id.renderButton(hitLayer, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text,
      shape: 'rectangular',
      logo_alignment: 'left',
      width,
    });

    return () => {
      hitLayer.innerHTML = '';
    };
  });
}

/**
 * Apple Sign In (system sheet / Apple modal on the current origin).
 * Resolves { idToken, fullName }.
 */
export async function requestAppleIdToken() {
  const clientId = getAppleClientId();
  const redirectURI = getAppleRedirectUri();
  if (!clientId || !redirectURI) {
    throw new Error('Apple sign-in is not configured');
  }

  await loadAppleIdScript();

  window.AppleID.auth.init({
    clientId,
    scope: 'name email',
    redirectURI,
    state: randomState(),
    nonce: randomState(),
    usePopup: true,
  });

  try {
    const response = await window.AppleID.auth.signIn();
    const idToken = response?.authorization?.id_token;
    if (!idToken) {
      throw new Error('Apple did not return an identity token');
    }

    return {
      idToken,
      fullName: response?.user?.name || null,
    };
  } catch (err) {
    if (err?.code === 'cancelled') throw err;
    const mapped = mapAppleAuthError(err);
    if (mapped === null) {
      const cancel = new Error('cancelled');
      cancel.code = 'cancelled';
      throw cancel;
    }
    const wrapped = new Error(mapped);
    wrapped.code = err?.error || err?.code || 'APPLE_AUTH';
    wrapped.cause = err;
    throw wrapped;
  }
}
